import * as React from 'react';
import { formatNumber, getSuggestedMaxTick, getMaxTickLimit } from '../../helpers/ChartHelper';
import CloseIcon from '@material-ui/icons/Close';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { makeStyles, Theme, createStyles, IconButton, FormGroup, FormControlLabel, Checkbox, Radio, RadioGroup } from '@material-ui/core';
import { ChartFilter } from '../ChartComponents';
import { SelectedItemProps } from '../MattersByStatusChart';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import {
    useScreenshot, 
    createFileName 
} from 'use-react-screenshot';
import MTooltip from '@material-ui/core/Tooltip';
import { ChartInfo } from '../../components/ChartInfo';
import { useCurrencySymbol } from '../../hooks/useCurrencySymbol';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { capitalizeFirstLetter } from '../../helpers/commonHelper';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { SearchCriteria } from '../SearchCriteria';
import { DrillDownType } from '../DrillDownType';

import { RV_CHART_CLASS, RV_CHART_COLORS, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvLegendLabels, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import InsertChartOutlinedIcon from '@material-ui/icons/InsertChartOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            // minHeight: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            [`& ${`span`}`]: {
                // marginBottom: '15px',
                // display: 'block',
                // textAlign: 'center',
                // with: '100%',
            },
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            [`& .${`titleWrapper`}`]: {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                [`& .${`title`}`]: {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                [`& .${`value`}`]: {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                [`& .${`secondary-title`}`]: {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
            '&:hover': {
                '& .scroll': {
                    display: 'flex !important',
                    '& .Mui-disabled': {
                        '& .MuiSvgIcon-root': {
                            color: 'inherit !important',
                        },
                    },
                },
            },
        },
        barTitleContainer: {
            display: 'flex',
            flexFlow: 'column',
            alignItems: 'center',
            padding: '15px 15px 0px 15px',
            fontSize: '16px',
            fontWeight: 600,
            [`& .${`h1`}`]: {},
            [`& .${`h2`}`]: {
                fontSize: '2.5rem',
                padding: '10px 0',
            },
            [`& .${`h3`}`]: {
                paddingTop: '10px',
            },
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            position: 'relative',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        footerDescription: {
            fontSize: '1rem',
            textAlign: 'center',
        },
        creditCount: {
            padding: '10px 0px',
            fontWeight: 600,
            textAlign: 'center',
            fontSize: '18px',
        },
        scrollUp: {
            position: 'absolute',
            top: '30px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        },
        scrollDown: {
            position: 'absolute',
            bottom: '90px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        },
        footerControl: {
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            gap: '10px',
            alignItems: 'center',
        },
        radioGroup: {
            justifyContent: 'center',
        }
    }),
);
interface ChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
}

interface ChartState extends Partial<WriteOffAndOn> {
    total: number;
    symbol: string;
    writeOns: boolean;
    writeOffs: boolean;
    enterWIP: boolean;
    generatedWIP: boolean;
    otherWIP: boolean;
    selectedOption: 'Staff' | 'Reason' | 'WIP Code';
    selectedPeriod: 'thisMonth' | 'previousMonth' | 'financialYear' | 'previousFinancialYear';
}

export const WriteOffOnChart: React.FC<ChartProps> = (props) => {
    const classes = useStyles();

    const currencyQuery = useCurrencySymbol();

    const writeOffOnQuery = useQuery<WriteOffOnData, WriteOffOnParams>(WriteOffOnQuery, {
        variables: {
            entityType: props.chartFilter && props.chartFilter.entityType ? props.chartFilter.entityType : null,
            entityGuid: props.chartFilter && props.chartFilter.entityGuid ? props.chartFilter.entityGuid : null,
            responsibility: props.chartFilter && props.chartFilter.responsibility ? props.chartFilter.responsibility : null,
            showAllRecords: props.showAllRecords
        },
    });

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const entireChartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollUpAndPrevRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollDownAndNextRef = React.useRef<any>();

    const [
        hideControl, 
        // setHideControl
    ] = React.useState<boolean>(false);

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0,
    })[1];

    const [state, setState] = React.useState<ChartState>({
        total: 0,
        symbol: '$',
        writeOffs: props.maximizeSelected ? props.maximizeSelected.writeOffs : true,
        writeOns: props.maximizeSelected ? props.maximizeSelected.writeOns : false,
        enterWIP: props.maximizeSelected ? props.maximizeSelected.enterWIP : true,
        generatedWIP: props.maximizeSelected ? props.maximizeSelected.generatedWIP : false,
        otherWIP: props.maximizeSelected ? props.maximizeSelected.otherWIP : false,
        selectedOption: props.maximizeSelected ? props.maximizeSelected.selectedOption : 'Reason',
        selectedPeriod: props.maximizeSelected ? props.maximizeSelected.selectedPeriod : 'thisMonth'
    });


    React.useEffect(() => {

        if (!writeOffOnQuery.loading && !writeOffOnQuery.error && writeOffOnQuery.data && writeOffOnQuery.data.statistics && writeOffOnQuery.data.statistics.fees && writeOffOnQuery.data.statistics.fees.writeOffAndOn) {
            setState((prevState) => {

                const { 
                    writeOffAndOnByGrouping,
                    enteredGeneratedWriteOffAndOnByGrouping,
                    enteredOtherWriteOffAndOnByGrouping,
                    enteredWriteOffAndOnByGrouping,
                    generatedWriteOffAndOnByGrouping,
                    geneneratedOtherWriteOffAndOnByGrouping,
                    otherWriteOffAndOnByGrouping,
                } = writeOffOnQuery.data!.statistics.fees.writeOffAndOn;

                return {
                    ...prevState,
                    enteredGeneratedWriteOffAndOnByGrouping,
                    writeOffAndOnByGrouping,
                    enteredOtherWriteOffAndOnByGrouping,
                    enteredWriteOffAndOnByGrouping,
                    generatedWriteOffAndOnByGrouping,
                    geneneratedOtherWriteOffAndOnByGrouping,
                    otherWriteOffAndOnByGrouping,
                }
            })
        }

    }, [writeOffOnQuery.data, writeOffOnQuery.loading, writeOffOnQuery.error]);

    React.useEffect(() => {
        window.addEventListener('keydown', onKeyDownHandler);

        return () => {
            window.removeEventListener('keydown', onKeyDownHandler);
        };
        // tslint:disable-next-line: align
    }, []);

    // tslint:disable-next-line: no-any
    const onKeyDownHandler = (event: any) => {
        if (event.keyCode === 80 && (event.ctrlKey || event.metaKey) && !event.altKey) {
            event.preventDefault();
            if (event.stopImmediatePropagation) {
                event.stopImmediatePropagation();
            } else {
                event.stopPropagation();
            }
            return;
        }
    };

    const onCloseChart = () => {
        if (props.onCloseChart) {
            return props.onCloseChart(`isWriteOffOnChartOpen`);
        }
    };

    const onScrollUpAndPrev = (type: 'up' | 'prev') => {
        if (chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min > 0) {
            const scrollRatio = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            const min = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            if (scrollRatio < chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min) {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= scrollRatio ? scrollRatio : 1;
            } else {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= min;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= min;
            }
            chartRef.current.options.scales[type === 'up' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            // enabled scroll up
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            // disabled scroll up
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }
        scrollDownAndNextRef.current.classList.remove('Mui-disabled');
    };

    const onScrollDownAndNext = (type: 'down' | 'next') => {
        const labels = getWriteOnOffGroup().map((item) => item.grouping)

        if (chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max < labels.length) {
            const remainingBar = labels.length - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max;
            const scrollRatio = chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min;
            if (remainingBar <= scrollRatio) {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += remainingBar;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += remainingBar;
            } else {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += scrollRatio ? scrollRatio : 1;
            }
            chartRef.current.options.scales[type === 'down' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            // enabled scroll down
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            // disabled scroll down
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
        scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
    };

    const onScollBar = (type: 'up' | 'down' | 'prev' | 'next') => () => {
        if (type === 'up') {
            onScrollUpAndPrev('up');
        } else if (type === 'down') {
            onScrollDownAndNext('down');
        } else if (type === 'next') {
            onScrollDownAndNext('next');
        } else {
            onScrollUpAndPrev('prev');
        }
    };

    const onDisabledEnabledArrow = (barType: 'horizontal' | 'vertical') => {
        const labels = getWriteOnOffGroup().map((item) => item.grouping)
        // up
        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].min > 0) {
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }

        // down
        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].max < labels.length) {
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
    };

    const onPan = (context: { chart: Chart }) => {
        onDisabledEnabledArrow('horizontal');

        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    const onZoom = (context: { chart: Chart }) => {
        onDisabledEnabledArrow('horizontal');

        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    const onMaximize = () => {
        if (props.onMaximizeSelected) {
            props.onMaximizeSelected({
                writeOffs: state.writeOffs,
                writeOns: state.writeOns,
                selectedOption: state.selectedOption,
                selectedPeriod: state.selectedPeriod,
                enterWIP: state.enterWIP,
                generatedWIP: state.generatedWIP,
                otherWIP: state.otherWIP,
            });
        }
    };

    // tslint:disable-next-line: no-any
    const onContextMenu = (event: any) => {
        event.preventDefault();
        if (event.button !== 2) {
            return false;
        }
        return true;
    };

    const onDownload = (name: string, extension: string = 'jpg') => (image: any) => {
        const a = document.createElement('a');
        a.href = image;
        a.download = createFileName(extension, name);
        a.click();
    };

    // tslint:disable-next-line: no-any
    const onDownloadScreenshot = () => {
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload('Write Off/On Chart'));
            // tslint:disable-next-line: align
        }, 500);
    };

    const handleWriteCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevstate) => {
            const newState = { ...prevstate, [event.target.name]: event.target.checked };
            
            // Check if both writeOns and writeOffs are false
            if (!newState.writeOns && !newState.writeOffs) {
                // Set the checkbox that was just changed to true
                (newState[event.target.name as keyof ChartState] as any) = true;
            }
            
            return newState;
        });
    };

    const handleWipCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevstate) => {
            const newState = { ...prevstate, [event.target.name]: event.target.checked };
            
            // Check if both writeOns and writeOffs are false
            if (!newState.enterWIP && !newState.generatedWIP && !newState.otherWIP) {
                // Set the checkbox that was just changed to true
                (newState[event.target.name as keyof ChartState] as any) = true;
            }
            
            return newState;
        });
    };

    const handleOptionRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevstate) => {
            return { ...prevstate, selectedOption: event.target.value as 'Staff' | 'Reason' | 'WIP Code' };
        });
    };

    const handlePeriodRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevstate) => {
            return { ...prevstate, selectedPeriod: event.target.value as 'thisMonth' | 'previousMonth' | 'financialYear' | 'previousFinancialYear' };
        });
    };

    const getSelectedPeriodLabel = (peroid: 'thisMonth' | 'previousMonth' | 'financialYear' | 'previousFinancialYear') => {
        switch (peroid) {
            case 'thisMonth':
                return 'This Month';
            case 'previousMonth':
                return 'Previous Month';
            case 'financialYear':
                return 'Financial Year';
            case 'previousFinancialYear':
                return 'Previous Financial Year';
            default:
                return '';
        }
    };

    const getWriteLabel = (writeOns: boolean, writeOffs: boolean) => {
        if (writeOns && writeOffs) {
            return 'Write Offs & Ons';
        } else if (writeOns) {
            return 'Write Ons';
        } else if (writeOffs) {
            return 'Write Offs';
        } else {
            return '';
        }
    };

    const getTotalBySummary = (writeOffAndOn: keyof WriteOffAndOn, writeOffAndOnByGrouping: keyof WriteOffAndOnByGrouping) => {

        if (state.writeOns && state.writeOffs) {
            switch (state.selectedPeriod) {
                case 'thisMonth':
                    return (
                        (state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOffSummary?.thisMonth || 0) -
                        (state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOnSummary?.thisMonth || 0)
                    );
                case 'previousMonth':
                    return (
                        (state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOffSummary?.previousMonth || 0) -
                        (state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOnSummary?.previousMonth || 0)
                    );
                case 'financialYear':
                    return (
                        (state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOffSummary?.financialYear || 0) -
                        (state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOnSummary?.financialYear || 0)
                    );
                case 'previousFinancialYear':
                    return (
                        (state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOffSummary?.previousFinancialYear || 0) -
                        (state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOnSummary?.previousFinancialYear || 0)
                    );
                default:
                    return 0;
            }
        } else if (state.writeOns && !state.writeOffs) {
            switch (state.selectedPeriod) {
                case 'thisMonth':
                    return state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOnSummary?.thisMonth || 0;
                case 'previousMonth':
                    return state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOnSummary?.previousMonth || 0;
                case 'financialYear':
                    return state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOnSummary?.financialYear || 0;
                case 'previousFinancialYear':
                    return state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOnSummary?.previousFinancialYear || 0;
                default:
                    return 0;
            }
        } else if (state.writeOffs && !state.writeOns) {
            switch (state.selectedPeriod) {
                case 'thisMonth':
                    return state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOffSummary?.thisMonth || 0;
                case 'previousMonth':
                    return state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOffSummary?.previousMonth || 0;
                case 'financialYear':
                    return state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOffSummary?.financialYear || 0;
                case 'previousFinancialYear':
                    return state[writeOffAndOn]?.[writeOffAndOnByGrouping]?.writeOffSummary?.previousFinancialYear || 0;
                default:
                    return 0;
            }
        } else {
            return 0;
        }
    };

    const getTotal = () => {
        if (state.otherWIP && state.enterWIP && state.generatedWIP) {
            if (state.selectedOption === 'Staff') {
                return getTotalBySummary('writeOffAndOnByGrouping', 'staff');
            } else if (state.selectedOption === 'Reason') {
                return getTotalBySummary('writeOffAndOnByGrouping', 'reason');
            } else {
                return getTotalBySummary('writeOffAndOnByGrouping', 'wipType');
            }
        } else if (state.otherWIP && state.enterWIP) {
            if (state.selectedOption === 'Staff') {
                return getTotalBySummary('enteredOtherWriteOffAndOnByGrouping','staff');
            } else if (state.selectedOption === 'Reason') {
                return getTotalBySummary('enteredOtherWriteOffAndOnByGrouping', 'reason');
            } else {
                return getTotalBySummary('enteredOtherWriteOffAndOnByGrouping', 'wipType');
            }
        } else if (state.otherWIP && state.generatedWIP) {
            if (state.selectedOption === 'Staff') {
                return getTotalBySummary('geneneratedOtherWriteOffAndOnByGrouping','staff');
            } else if (state.selectedOption === 'Reason') {
                return getTotalBySummary('geneneratedOtherWriteOffAndOnByGrouping', 'reason');
            } else {
                return getTotalBySummary('geneneratedOtherWriteOffAndOnByGrouping', 'wipType');
            }
        } else if (state.enterWIP && state.generatedWIP) {
            if (state.selectedOption === 'Staff') {
                return getTotalBySummary('enteredGeneratedWriteOffAndOnByGrouping','staff');
            } else if (state.selectedOption === 'Reason') {
                return getTotalBySummary('enteredGeneratedWriteOffAndOnByGrouping', 'reason');
            } else {
                return getTotalBySummary('enteredGeneratedWriteOffAndOnByGrouping', 'wipType');
            }
        } else if (state.otherWIP) {
            if (state.selectedOption === 'Staff') {
                return getTotalBySummary('otherWriteOffAndOnByGrouping','staff');
            } else if (state.selectedOption === 'Reason') {
                return getTotalBySummary('otherWriteOffAndOnByGrouping', 'reason');
            } else {
                return getTotalBySummary('otherWriteOffAndOnByGrouping', 'wipType');
            }
        } else if (state.enterWIP) {
            if (state.selectedOption === 'Staff') {
                return getTotalBySummary('enteredWriteOffAndOnByGrouping','staff');
            } else if (state.selectedOption === 'Reason') {
                return getTotalBySummary('enteredWriteOffAndOnByGrouping', 'reason');
            } else {
                return getTotalBySummary('enteredWriteOffAndOnByGrouping', 'wipType');
            }
        // state.generatedWIP
        } else {
            if (state.selectedOption === 'Staff') {
                return getTotalBySummary('generatedWriteOffAndOnByGrouping','staff');
            } else if (state.selectedOption === 'Reason') {
                return getTotalBySummary('generatedWriteOffAndOnByGrouping', 'reason');
            } else {
                return getTotalBySummary('generatedWriteOffAndOnByGrouping', 'wipType');
            }
        }
    };

    const drillDownDataSet = () => {
        if (state.otherWIP && state.enterWIP && state.generatedWIP) {
            return null;                                                                                                  
        } else if (state.otherWIP && state.enterWIP) {
            return 'Entered,Other';
        } else if (state.otherWIP && state.generatedWIP) {
            return 'Generated,Other';
        } else if (state.enterWIP && state.generatedWIP) {
            return 'Entered,Generated';
        } else if (state.otherWIP) {
            return 'Other';
        } else if (state.enterWIP) {
            return 'Entered'
        } else {
            return 'Generated'
        }
    }

    const getWriteOffAndOnByGroupingSummary = (writeOffAndOn: keyof WriteOffAndOn, writeOffAndOnByGrouping: keyof WriteOffAndOnByGrouping) => {
        if (!state[writeOffAndOn] || !state[writeOffAndOn]?.[writeOffAndOnByGrouping]) {
            return [];
        }
        switch (state.selectedPeriod) {
            case 'thisMonth':
                return state[writeOffAndOn]?.[writeOffAndOnByGrouping].thisMonth || [];
            case 'previousMonth':
                return state[writeOffAndOn]?.[writeOffAndOnByGrouping].previousMonth || [];
            case 'financialYear':
                return state[writeOffAndOn]?.[writeOffAndOnByGrouping].financialYear || [];
            case 'previousFinancialYear':
                return state[writeOffAndOn]?.[writeOffAndOnByGrouping].previousFinancialYear || [];
            default:
                return [];
        }
    };

    const getWriteOnOffGroup = () => {

        if (state.otherWIP && state.enterWIP && state.generatedWIP) {
            if (state.selectedOption === 'Staff') {
                return getWriteOffAndOnByGroupingSummary('writeOffAndOnByGrouping','staff');
            } else if (state.selectedOption === 'Reason') {
                return getWriteOffAndOnByGroupingSummary('writeOffAndOnByGrouping', 'reason');
            } else {
                return getWriteOffAndOnByGroupingSummary('writeOffAndOnByGrouping', 'wipType');
            }
        } else if (state.otherWIP && state.enterWIP) {
            if (state.selectedOption === 'Staff') {
                return getWriteOffAndOnByGroupingSummary('enteredOtherWriteOffAndOnByGrouping','staff');
            } else if (state.selectedOption === 'Reason') {
                return getWriteOffAndOnByGroupingSummary('enteredOtherWriteOffAndOnByGrouping', 'reason');
            } else {
                return getWriteOffAndOnByGroupingSummary('enteredOtherWriteOffAndOnByGrouping', 'wipType');
            }
        } else if (state.otherWIP && state.generatedWIP) {
            if (state.selectedOption === 'Staff') {
                return getWriteOffAndOnByGroupingSummary('geneneratedOtherWriteOffAndOnByGrouping','staff');
            } else if (state.selectedOption === 'Reason') {
                return getWriteOffAndOnByGroupingSummary('geneneratedOtherWriteOffAndOnByGrouping', 'reason');
            } else {
                return getWriteOffAndOnByGroupingSummary('geneneratedOtherWriteOffAndOnByGrouping', 'wipType');
            }
        } else if (state.enterWIP && state.generatedWIP) {
            if (state.selectedOption === 'Staff') {
                return getWriteOffAndOnByGroupingSummary('enteredGeneratedWriteOffAndOnByGrouping','staff');
            } else if (state.selectedOption === 'Reason') {
                return getWriteOffAndOnByGroupingSummary('enteredGeneratedWriteOffAndOnByGrouping', 'reason');
            } else {
                return getWriteOffAndOnByGroupingSummary('enteredGeneratedWriteOffAndOnByGrouping', 'wipType');
            }
        } else if (state.otherWIP) {
            if (state.selectedOption === 'Staff') {
                return getWriteOffAndOnByGroupingSummary('otherWriteOffAndOnByGrouping','staff');
            } else if (state.selectedOption === 'Reason') {
                return getWriteOffAndOnByGroupingSummary('otherWriteOffAndOnByGrouping', 'reason');
            } else {
                return getWriteOffAndOnByGroupingSummary('otherWriteOffAndOnByGrouping', 'wipType');
            }
        } else if (state.enterWIP) {
            if (state.selectedOption === 'Staff') {
                return getWriteOffAndOnByGroupingSummary('enteredWriteOffAndOnByGrouping','staff');
            } else if (state.selectedOption === 'Reason') {
                return getWriteOffAndOnByGroupingSummary('enteredWriteOffAndOnByGrouping', 'reason');
            } else {
                return getWriteOffAndOnByGroupingSummary('enteredWriteOffAndOnByGrouping', 'wipType');
            }
        // state.generatedWIP
        } else {
            if (state.selectedOption === 'Staff') {
                return getWriteOffAndOnByGroupingSummary('generatedWriteOffAndOnByGrouping','staff');
            } else if (state.selectedOption === 'Reason') {
                return getWriteOffAndOnByGroupingSummary('generatedWriteOffAndOnByGrouping', 'reason');
            } else {
                return getWriteOffAndOnByGroupingSummary('generatedWriteOffAndOnByGrouping', 'wipType');
            }
        }
    };

    const handleTotalClick = () => {
        if (props.getSelectedItem) {
            // const searchCriterias = new Array<SearchCriteria>();
            const searchCriterias = new Array<SearchCriteria>();

            if (state.selectedPeriod === 'thisMonth') {
                searchCriterias.push({ property: 'Period', operator: 'curr', comparison: 'month' });
            } else if (state.selectedPeriod === 'previousMonth') {
                searchCriterias.push({ property: 'Period', operator: 'prev', comparison: 'month' });
            } else if (state.selectedPeriod === 'financialYear') {
                searchCriterias.push({ property: 'Period', operator: 'curr', comparison: 'fy' });
            } else if (state.selectedPeriod === 'previousFinancialYear') {
                searchCriterias.push({ property: 'Period', operator: 'prev', comparison: 'fy' });
            }

            if (state.writeOns && !state.writeOffs) {
                searchCriterias.push({ property: 'WriteOn', operator: 'gt', comparison: '0' });
            }
            else if (state.writeOffs && !state.writeOns) {
                searchCriterias.push({ property: 'WriteOff', operator: 'gt', comparison: '0' });
            }

            var drilldownDataSet = drillDownDataSet();
            if (drilldownDataSet !== null){
                searchCriterias.push({ property: 'WipSource', operator: 'in', comparison: drillDownDataSet() ?? 'null' });
            } else {
                searchCriterias.push({ property: 'WipSource', operator: 'isnot', comparison: 'null' });
            }

            props.getSelectedItem(searchCriterias, true, `Drilldown from Write Off/On Chart by ${state.selectedOption} - ${getSelectedPeriodLabel(state.selectedPeriod)}`, DrillDownType.writeOnOffSummaryDrilldown);
        }
    };

    const getDataset = () => {
        if (state.writeOns && state.writeOffs) {
            return [
                {
                    label: 'Write Off',
                    ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                    data: getWriteOnOffGroup().map((item) => item.writeOff),
                },
                {
                    label: 'Write On',
                    ...rvBarDatasetStyle(RV_CHART_COLORS.amber),
                    data: getWriteOnOffGroup().map((item) => item.writeOn),
                }
            ];
        } else if (state.writeOns && !state.writeOffs) {
            return [
                {
                    label: 'Write On',
                    ...rvBarDatasetStyle(RV_CHART_COLORS.amber),
                    data: getWriteOnOffGroup().map((item) => item.writeOn),
                }
            ];
        } else if (!state.writeOns && state.writeOffs) {
            return [
                {
                    label: 'Write Off',
                    ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                    data: getWriteOnOffGroup().map((item) => item.writeOff),
                }
            ];
        }

        return [];
    };

    const getDataValues = () => {
        if (state.writeOns && state.writeOffs) {
            const tempWriteOffValues = getWriteOnOffGroup().map((item) => item.writeOff);
            const tempWriteOnValues = getWriteOnOffGroup().map((item) => item.writeOn);
            const combinedValues = [...tempWriteOffValues, ...tempWriteOnValues];
            return combinedValues;
        } else if (state.writeOns && !state.writeOffs) {
            return getWriteOnOffGroup().map((item) => item.writeOn);
        } else if (!state.writeOns && state.writeOffs) {
            return getWriteOnOffGroup().map((item) => item.writeOff);
        } else {
            return [];
        }
    };

    const chartControlComponent = () => {
        return (
            <>
                <IconButtonWrapper aria-label="close">
                    <ChartInfo description={props.description} fontSize={props.showAllRecords ? 'large' : 'medium'} className={`${classes.helpIcon} rv-chart-help`} />
                    <IconButton size={props.showAllRecords ? 'medium' : 'small'} onClick={onDownloadScreenshot}>
                        <GetAppOutlinedIcon color="primary" fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                    {!props.showAllRecords && props.onMaximizeSelected && (
                        <MTooltip title="Expand chart">
                            <IconButton aria-label="maximize" onClick={onMaximize} size="small">
                                <AspectRatioIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };

    if (currencyQuery.loading || writeOffOnQuery.loading) {
        return (
            <div className="rv-chart rv-chart-loading">
                {chartControlComponent()}
                <ChartSkeleton variant="bars" />
            </div>
        );
    }

    const symbol = currencyQuery.data?.options.currencySymbol.symbol || '$';

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}`} ref={entireChartRef}>
            {hideControl ? null : chartControlComponent()}
            <div className={classes.barTitleContainer}>
                <ChartHeading icon={<InsertChartOutlinedIcon />} title={<>{getWriteLabel(state.writeOns, state.writeOffs)} by {capitalizeFirstLetter(state.selectedOption)}</>} />
                <div className={`h2 ${classes.totalDiv}`} onClick={() => handleTotalClick()}>
                    <AnimatedNumber value={getTotal()} format={(amount) => `${symbol}${formatNumber(amount)}`} />
                </div>
                <div className="h3">
                    {getSelectedPeriodLabel(state.selectedPeriod)}
                </div>
            </div>
            <div className={classes.barContainer} style={{ maxHeight: props.showAllRecords ? '100%' : '350px' }}>
                {props.showAllRecords && (
                    <>
                        <div className={`${classes.scrollUp} scroll`}>
                            <IconButton size="medium" onClick={onScollBar('up')} ref={scrollUpAndPrevRef}>
                                <KeyboardArrowUpIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                        <div className={`${classes.scrollDown} scroll`}>
                            <IconButton size="medium" onClick={onScollBar('down')} ref={scrollDownAndNextRef}>
                                <KeyboardArrowDownIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                    </>
                )}
                <Bar
                    ref={chartRef}
                    onContextMenu={onContextMenu}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;
                            const searchCriterias = new Array<SearchCriteria>();
                            const item = getWriteOnOffGroup().map((item) => item.grouping)[_index]
                            //const item = getWriteOnOffGroup().map((item) => item.grouping)[_index];
                            
                            if (state.selectedPeriod === 'thisMonth') {
                                searchCriterias.push({ property: 'Period', operator: 'curr', comparison: 'month' });
                            } else if (state.selectedPeriod === 'previousMonth') {
                                searchCriterias.push({ property: 'Period', operator: 'prev', comparison: 'month' });
                            } else if (state.selectedPeriod === 'financialYear') {
                                searchCriterias.push({ property: 'Period', operator: 'curr', comparison: 'fy' });
                            } else if (state.selectedPeriod === 'previousFinancialYear') {
                                searchCriterias.push({ property: 'Period', operator: 'prev', comparison: 'fy' });
                            }

                            if (state.writeOns && !state.writeOffs) {
                                searchCriterias.push({ property: 'WriteOn', operator: 'gt', comparison: '0' });
                            }
                            else if (state.writeOffs && !state.writeOns) {
                                searchCriterias.push({ property: 'WriteOff', operator: 'gt', comparison: '0' });
                            }

                            var type: string = state.selectedOption.toString();

                            if (type === 'WIP Code') {
                                type = 'WipType'
                            }

                            searchCriterias.push(
                                { property: type, operator: 'eq', comparison: item },
                            );

                            var drilldownDataSet = drillDownDataSet();
                            if (drilldownDataSet !== null){
                                searchCriterias.push({ property: 'WipSource', operator: 'in', comparison: drillDownDataSet() ?? 'null' });
                            } else {
                                searchCriterias.push({ property: 'WipSource', operator: 'isnot', comparison: 'null' });
                            }

                            props.getSelectedItem(searchCriterias, true, `Drilldown from Write Off/On Chart by ${state.selectedOption} - ${item}`, DrillDownType.writeOnOffSummaryDrilldown);
                        }
                    }}
                    data={{
                        labels: getWriteOnOffGroup().map((item) => item.grouping),
                        datasets: getDataset(),
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'y' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick(getDataValues()),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit(getDataValues()),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${state.symbol}${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            y: {
                                grid: rvCategoryAxisStyle.grid,
                                min: 0,
                                max: props.showAllRecords ? 29 : 9,
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                },
                            },
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: true,
                                labels: rvLegendLabels,
                            },
                            title: {
                                display: false
                            },
                            zoom: {
                                pan: {
                                    enabled: props.showAllRecords,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: props.showAllRecords,
                                    },
                                    onZoom: onZoom,
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },

                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            // dataIndex,
                                            // label,
                                            formattedValue,
                                            dataset
                                        } = context;

                                        console.log('context', context);

                                        return `${dataset.label}: ${state.symbol}${formattedValue}`;
                                    },
                                },
                            },
                        },
                    }}
                />
            </div>
            <div
                className={classes.footerControl}
            >
                <FormGroup 
                    row={true}
                    className={classes.radioGroup}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={state.enterWIP}
                                onChange={handleWipCheckboxChange}
                                name="enterWIP"
                                color="primary"
                            />
                        }
                        label="Entered WIP"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={state.generatedWIP}
                                onChange={handleWipCheckboxChange}
                                name="generatedWIP"
                                color="primary"
                            />
                        }
                        label="Generated WIP"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={state.otherWIP}
                                onChange={handleWipCheckboxChange}
                                name="otherWIP"
                                color="primary"
                            />
                        }
                        label="Other"
                    />

                </FormGroup>

                <FormGroup 
                    row={true}
                    className={classes.radioGroup}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={state.writeOns}
                                onChange={handleWriteCheckboxChange}
                                name="writeOns"
                                color="primary"
                            />
                        }
                        label="Write Ons"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={state.writeOffs}
                                onChange={handleWriteCheckboxChange}
                                name="writeOffs"
                                color="primary"
                            />
                        }
                        label="Write Offs"
                    />
                </FormGroup>

                <RadioGroup 
                    row={true}
                    className={classes.radioGroup}
                    aria-label="selectedOption" 
                    name="selectedOption" 
                    value={state.selectedOption} 
                    onChange={handleOptionRadioChange}
                >
                    <FormControlLabel value="Reason" control={<Radio  color="primary" />} label="Reason" />
                    <FormControlLabel value="Staff" control={<Radio color="primary" />} label="Staff" />
                    <FormControlLabel value="WIP Code" control={<Radio  color="primary" />} label="WIP Code" />
                </RadioGroup>

                <RadioGroup 
                    row={true}
                    className={classes.radioGroup}
                    aria-label="selectedPeriod" 
                    name="selectedPeriod" 
                    value={state.selectedPeriod} 
                    onChange={handlePeriodRadioChange}
                >
                    <FormControlLabel value="thisMonth" control={<Radio color="primary" />} label="This Month" />
                    <FormControlLabel value="previousMonth" control={<Radio  color="primary" />} label="Previous Month" />
                    <FormControlLabel value="financialYear" control={<Radio  color="primary" />} label="Financial Year" />
                    <FormControlLabel value="previousFinancialYear" control={<Radio  color="primary" />} label="Previous Financial Year" />
                </RadioGroup>
            </div>

        </div>
    );
};

export interface WriteOffOnParams {
    entityType: number | null;
    entityGuid: string | null;
    responsibility: number | null;
    showAllRecords?: boolean;
}

export type WriteOffOnData = {
    statistics: Statistics;
};

export type Statistics = {
    fees: Fees;
}

export type Fees = {
    writeOffAndOn: WriteOffAndOn;
}

export type WriteOffAndOn = {
    writeOffAndOnByGrouping:                 WriteOffAndOnByGrouping;
    enteredWriteOffAndOnByGrouping:          WriteOffAndOnByGrouping;
    generatedWriteOffAndOnByGrouping:        WriteOffAndOnByGrouping;
    otherWriteOffAndOnByGrouping:            WriteOffAndOnByGrouping;
    enteredGeneratedWriteOffAndOnByGrouping: WriteOffAndOnByGrouping;
    enteredOtherWriteOffAndOnByGrouping:     WriteOffAndOnByGrouping;
    geneneratedOtherWriteOffAndOnByGrouping: WriteOffAndOnByGrouping;
}

export type WriteOffAndOnByGrouping = {
    staff:   WriteOnOffByGrouping;
    reason:  WriteOnOffByGrouping;
    wipType: WriteOnOffByGrouping;
}

export type WriteOnOffByGrouping = {
    thisMonth:             WriteOnOffGroup[];
    previousMonth:         WriteOnOffGroup[];
    financialYear:         WriteOnOffGroup[];
    previousFinancialYear: WriteOnOffGroup[];
    writeOnSummary:        WriteOSummary;
    writeOffSummary:       WriteOSummary;
}

export type WriteOnOffGroup = {
    id:       number;
    grouping: string;
    writeOn:  number;
    writeOff: number;
}

export type WriteOSummary = {
    id:                    number;
    thisMonth:             number;
    previousMonth:         number;
    financialYear:         number;
    previousFinancialYear: number;
}

const WriteOffOnQuery = gql`
    fragment WriteOnOffGroup on WriteOffAndOnGrouped {
        grouping
        writeOn
        writeOff
    }

    fragment WriteOnOffGroupSummary on WriteOffAndOnSummary {
        thisMonth
        previousMonth
        financialYear
        previousFinancialYear
    }

    fragment WriteOnOffByGroup on WriteOffAndOnByGroup {
        thisMonth {
            ... WriteOnOffGroup
        }
        previousMonth {
            ... WriteOnOffGroup
        }
        financialYear {
            ... WriteOnOffGroup
        }
        previousFinancialYear {
            ... WriteOnOffGroup
        }
        writeOnSummary {
            ... WriteOnOffGroupSummary
        }
        writeOffSummary {
            ... WriteOnOffGroupSummary
        }
    }

    fragment WriteOnOffByGrouping on writeOffAndOnbyGrouping {
        staff {
            ... WriteOnOffByGroup
        }
        reason {
            ... WriteOnOffByGroup
        }
        wipType {
            ... WriteOnOffByGroup
        }
    }

    query writeOffAndOnByGroup($entityType: Int, $entityGuid: String, $responsibility: Int, $showAllRecords: Boolean) {
        statistics {
            fees {
                writeOffAndOn(entityGuid: $entityGuid, entityType: $entityType, responsibility: $responsibility,showAllRecords: $showAllRecords) {
                    writeOffAndOnByGrouping {
                        ... WriteOnOffByGrouping
                    }
                    enteredWriteOffAndOnByGrouping {
                        ... WriteOnOffByGrouping
                    }
                    generatedWriteOffAndOnByGrouping {
                        ... WriteOnOffByGrouping
                    }
                    otherWriteOffAndOnByGrouping {
                        ... WriteOnOffByGrouping
                    }
                    enteredGeneratedWriteOffAndOnByGrouping {
                        ... WriteOnOffByGrouping
                    }
                    enteredOtherWriteOffAndOnByGrouping {
                        ... WriteOnOffByGrouping
                    }
                    geneneratedOtherWriteOffAndOnByGrouping {
                        ... WriteOnOffByGrouping
                    }
                }
            }
        }
    }
`;
